import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    Group,
    MicrosoftAuthenticatorAuthenticationMethodConfiguration,
    SmsAuthenticationMethodConfiguration,
    EmailAuthenticationMethodConfiguration,
    TemporaryAccessPassAuthenticationMethodConfiguration,
} from '@microsoft/microsoft-graph-types-beta';
import { take } from 'rxjs';
import { ManageItemsWrapperDialogComponent } from '../../conditional-access-policy/shared/manage-items-wrapper-dialog/manage-items-wrapper-dialog.component';
import { CapItem, RowType } from '../../conditional-access-policy/shared/model';
import { TemplateItem } from 'projects/angular-clarity/src/app/modules/sway/store/templates/items/model';
import { IncludeOptionsLookups } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/authentication-method-policy-utlity';
import { AuthenticationMethodPolicyMicrosoftAuthenticatorForm } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/microsoft-authenticator/model';

type PolicyItem =
    | MicrosoftAuthenticatorAuthenticationMethodConfiguration
    | SmsAuthenticationMethodConfiguration
    | EmailAuthenticationMethodConfiguration
    | TemporaryAccessPassAuthenticationMethodConfiguration;

@Component({
    selector: 'app-authentication-method-policy-targets',
    templateUrl: './authentication-method-policy-targets.component.html',
    styleUrls: ['./authentication-method-policy-targets.component.scss', '../../../styles/baseline-style.scss'],
})
export class AuthenticationMethodPolicyTargetsComponent {
    @Input() tenantId: string;
    @Input() policy: PolicyItem | any;
    @Input() errorsString: string;
    @Input() form: FormGroup;
    @Input() item: TemplateItem;
    @Input() groups: Map<string, Group> = new Map();

    @Input() includeOptionsLookups: IncludeOptionsLookups[] = [];

    @Input() override: string[];
    @Input() readOnly: boolean;
    @Input() showIsUsableForSignIn: boolean = false;

    includeOptionValues = {
        any: 'Any',
        deviceBasedPush: 'Passwordless',
        push: 'Push',
    };

    constructor(
        private dialog: MatDialog,
        private fb: FormBuilder,
    ) {}

    trackByGroupId(index: number, group: any): any {
        return group.id;
    }
    trackByColumnKey(index: number, column: any): any {
        return column.key;
    }

    get includeTargetsValue() {
        return this.formValue.includeTargets;
    }

    get excludeTargetsValue() {
        return this.formValue.excludeTargets;
    }

    get formValue(): AuthenticationMethodPolicyMicrosoftAuthenticatorForm {
        return this.form.getRawValue();
    }

    manageGroups(type: 'Exclude' | 'Include'): void {
        const lookup = Array.from(this.groups.values());
        const config = {
            title: 'Groups',
            allListTitle: 'Available groups',
            desiredListTitle: type === 'Include' ? 'Included groups in baseline' : 'Excluded groups in baseline',
            allListComparativeTitle: type === 'Include' ? 'Tenant included groups' : 'Tenant excluded groups',
            type: type === 'Include' ? RowType.includeGroups : RowType.excludeGroups,
        };

        const policyItems: string[] =
            type === 'Include'
                ? this.policy.includeTargets.map((res) => res.id)
                : this.policy.excludeTargets.map((res) => res.id);

        const baselineItems =
            type === 'Include'
                ? this.formValue.includeTargets.includedGroups.map((res) => res.id) || []
                : this.formValue.excludeTargets || [];

        let filteredLookup: Group[];
        if (type === 'Include') {
            const excludedGroupsInBaseline = this.formValue.excludeTargets || [];
            filteredLookup = lookup.filter((res) => !excludedGroupsInBaseline.some((b_item) => b_item === res.id));
        } else {
            const includedGroupsInBaseline = this.formValue.includeTargets.includedGroups.map((res) => res.id) || [];

            filteredLookup = lookup.filter((res) => !includedGroupsInBaseline.some((b_item) => b_item === res.id));
        }

        const allItems: CapItem[] = filteredLookup.map((lookup) => {
            const inBaselineSchema = baselineItems?.some((baselineItem) => baselineItem === lookup.id);

            return {
                displayName: lookup.displayName,
                id: lookup.id,
                inBaselineSchema,
                inPolicy: policyItems?.some((policyItem) => policyItem === lookup.id),
            };
        });

        const dialogRef = this.dialog.open(ManageItemsWrapperDialogComponent, {
            data: {
                allItems: allItems,
                ...config,
            },
            maxHeight: '90vh',
            width: '70%',
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((res: CapItem[]) => {
                if (res instanceof Array) {
                    const schemaItems = res.filter((res) => res.inBaselineSchema) || [];

                    if (type === 'Include') {
                        const includedGroups = this.fb.array(
                            schemaItems.map((res) => {
                                const group: any = {
                                    id: [res.id],
                                    authenticationMode: ['any'],
                                };
                                if (this.showIsUsableForSignIn) {
                                    group.isUsableForSignIn = [false];
                                }
                                return this.fb.group(group);
                            }),
                        );

                        const includeTargets: FormGroup = this.form.get('includeTargets') as FormGroup;
                        // Replace the whole form array
                        includeTargets.setControl('includedGroups', includedGroups);
                    } else {
                        this.form.get('excludeTargets').setValue(schemaItems.map((res) => res.id));
                    }
                }
            });
    }

    onChangeIncludedTargets() {
        if (!this.form.value.includeTargets.groupInclusion) {
            const group: any = {
                id: ['all_users'],
                authenticationMode: ['any'],
            };
            if (this.showIsUsableForSignIn) {
                group.isUsableForSignIn = [false];
            }
            const includedGroups = this.fb.array([this.fb.group(group)]);

            const includeTargets: FormGroup = this.form.get('includeTargets') as FormGroup;
            // Replace the whole form array
            includeTargets.setControl('includedGroups', includedGroups);
        }

        // else, we keep it for all users until the add anything
    }
}
